import React, { useEffect, useState } from 'react'
import 'leaflet/dist/leaflet.css'

import {
  EventResult,
  EventResultObject,
  Game,
  GameProperty,
  MemberResponse,
  MitOhne,
  Round,
  Severity,
} from '../../../../shared/types'
import { Box, Grid, IconButton, TableCell, Theme, Typography, useMediaQuery, useTheme } from '@mui/material'
import DropDownComponent, { DropDownElement } from '../../../../shared/components/DropDownComponent'
import { useDispatch, useSelector } from 'react-redux'
import { getEventResults, getMembers } from '../../../../store/data/data.selectors'
import CheckBoxComponent from '../../../../shared/components/CheckBoxComponent'
import * as settingsActions from '../../../../store/settings/settings.actions'
import SaveIcon from '@mui/icons-material/Save'
import { useTranslation } from 'react-i18next'
import Env from '../../../../shared/config/envConfig'
import { getApiVersion } from '../../../../shared/config/getApiVersion'
import * as dataActions from '../../../../store/data/data.actions'
import TextInputComponent from '../../../../shared/components/TextInputComponent'

interface EditResultRoundProps {
  round: Round
  roundNumber: number
  id?: string
  gameNumber: number
  dealer?: string
  players: string[]
  eventId: string
  eventResult: EventResult
  advancedEdit: boolean
}

const EditResultRound: React.FC<EditResultRoundProps> = (props) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const restPath = Env.restBase
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')

  const theme: Theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))

  const members: MemberResponse | undefined = useSelector(getMembers)
  const eventResults: EventResultObject | undefined = useSelector(getEventResults)

  const player4 = props.players[3] ? props.players[3] : undefined

  const [game, setGame] = useState<Game | undefined>(props.round.game)
  const [player, setPlayer] = useState<string | undefined>(props.round.player)
  const [factor, setFactor] = useState<number | undefined>(props.round.factor)
  const [mitOhne, setMitOhne] = useState<MitOhne | undefined>(props.round.mitOhne)
  const [gameProperty, setGameProperty] = useState<GameProperty | undefined>(props.round.gameProperty || GameProperty.einfach)
  const [win, setWin] = useState<boolean | undefined>(props.round.won)
  const [hand, setHand] = useState<boolean | undefined>(props.round.hand)

  const [player1Result, setPlayer1Result] = useState<number>(props.round.results[props.players[0]] || 0)
  const [player2Result, setPlayer2Result] = useState<number>(props.round.results[props.players[1]] || 0)
  const [player3Result, setPlayer3Result] = useState<number>(props.round.results[props.players[2]] || 0)
  const [player4Result, setPlayer4Result] = useState<number>(props.round.results[props.players[3]] || 0)

  useEffect(() => {
    setGamePropertyOptions(getGamePropertyOptions())
  }, [game])

  const getGameOptions = (): DropDownElement[] => {
    return [
      {
        key: Game.eichel,
        value: 'Eichel',
      },
      {
        key: Game.gruen,
        value: 'Grün',
      },
      {
        key: Game.rot,
        value: 'Rot',
      },
      {
        key: Game.schell,
        value: 'Schell',
      },
      {
        key: Game.grand,
        value: 'Grand',
      },
      {
        key: Game.zero,
        value: 'Null',
      },
      {
        key: Game.eingemischt,
        value: 'eingemischt',
      },
    ]
  }

  const getMitOhneOptions = (): DropDownElement[] => {
    return [
      {
        key: '1',
        value: 'Ohne 4',
      },
      {
        key: '2',
        value: 'Ohne 3',
      },
      {
        key: '3',
        value: 'Ohne 2',
      },
      {
        key: '4',
        value: 'Ohne 1',
      },
      {
        key: '5',
        value: 'Mit 1',
      },
      {
        key: '6',
        value: 'Mit 2',
      },
      {
        key: '7',
        value: 'Mit 3',
      },
      {
        key: '8',
        value: 'Mit 4',
      },
    ]
  }

  const getGamePropertyOptions = (): DropDownElement[] => {
    if (game === Game.zero) {
      return [
        {
          key: GameProperty.einfach,
          value: '-',
        },
        {
          key: GameProperty.hand,
          value: 'Hand',
        },
        {
          key: GameProperty.ouvert,
          value: 'Ouvert',
        },
        {
          key: GameProperty.handOuvert,
          value: 'Hand Ouvert',
        },
      ]
    } else {
      return [
        {
          key: GameProperty.einfach,
          value: 'Einfach',
        },
        {
          key: GameProperty.schneider,
          value: 'Schneider',
        },
        {
          key: GameProperty.schwarz,
          value: 'Schwarz',
        },
        {
          key: GameProperty.hand,
          value: 'Hand',
        },
        {
          key: GameProperty.handSchneider,
          value: 'Hand Schneider',
        },
        {
          key: GameProperty.handSchwarz,
          value: 'Hand Schwarz',
        },
        {
          key: GameProperty.handSchneiderAngesagt,
          value: 'Hand Schneider angesagt',
        },
        {
          key: GameProperty.handSchneiderAngesagtSchwarz,
          value: 'Hand Schneider angesagt Schwarz',
        },
        {
          key: GameProperty.handSchwarzAngesagt,
          value: 'Hand Schwarz angesagt',
        },
        {
          key: GameProperty.handOuvert,
          value: 'Hand Ouvert',
        },
      ]
    }
  }

  const gameOptions: DropDownElement[] = getGameOptions()
  const mitOhneOptions: DropDownElement[] = getMitOhneOptions()
  const [gamePropertyOptions, setGamePropertyOptions] = useState<DropDownElement[]>(getGamePropertyOptions())

  const getGameValue = () => {
    let gameValue = 0
    switch (game) {
      case Game.grand:
        gameValue = 24
        break
      case Game.zero:
        gameValue = 23
        break
      case Game.eichel:
        gameValue = 12
        break
      case Game.gruen:
        gameValue = 11
        break
      case Game.rot:
        gameValue = 10
        break
      case Game.schell:
        gameValue = 9
        break
      case Game.eingemischt:
        gameValue = 0
        break
    }

    let propertyFactor = 0
    switch (gameProperty) {
      case GameProperty.einfach:
        propertyFactor = 0
        break
      case GameProperty.schneider:
        propertyFactor = 1
        break
      case GameProperty.schwarz:
        propertyFactor = 2
        break
      case GameProperty.ouvert:
        propertyFactor = 0
        break
      case GameProperty.hand:
        propertyFactor = 1
        break
      case GameProperty.handSchneider:
        propertyFactor = 2
        break
      case GameProperty.handSchwarz:
        propertyFactor = 3
        break
      case GameProperty.handSchneiderAngesagt:
        propertyFactor = 3
        break
      case GameProperty.handSchneiderAngesagtSchwarz:
        propertyFactor = 4
        break
      case GameProperty.handSchwarzAngesagt:
        propertyFactor = 5
        break
      case GameProperty.handOuvert:
        propertyFactor = 6
        break
    }

    if (game === Game.zero) {
      return gameProperty === GameProperty.hand ? 35 : (gameProperty === GameProperty.ouvert ? 46 : (gameProperty === GameProperty.handOuvert ? 59 : 23))
    } else {
      return factor ? gameValue * (factor + 1 + propertyFactor) : 0
    }
  }

  const getGamerOptions = () => {
    const options: DropDownElement[] = []
    props.players?.map(elem => {
      const member = members?.results.find(member => member.originId === elem)
      member && options.push({
        key: elem,
        value: `${member.firstname} ${member.lastname.charAt(0)}.`,
      })
    })
    return options
  }

  const saveResult = () => {
    const validGame = true
    if (validGame) {
      const value = win ? getGameValue() : (hand ? getGameValue() * -1 : getGameValue() * -2)

      // neues Spiel in DB speichern
      const restUrl = `${restPath}/api/${getApiVersion()}/game`
      props?.id && fetch(restUrl,
        {
          method: props.round.id ? 'PUT' : 'POST',
          body: JSON.stringify(
            {
              game: {
                id: props.round.id,
                roundId: props?.id,
                gameNumber: props.gameNumber,
                gameValue: value,
                gameProperty: gameProperty,
                mitOhne: mitOhne,
                hand: hand,
                game: game,
                won: win,
                factor: factor,
                player: player,
                eventId: props.eventId,
                player1Result: props.advancedEdit ? player1Result : null,
                player2Result: props.advancedEdit ? player2Result : null,
                player3Result: props.advancedEdit ? player3Result : null,
                player4Result: props.advancedEdit ? player4Result : null,
              },
            }),
          credentials: 'include',
          headers: requestHeaders,
        })
        .then(res => res.json())
        .then((json) => {
          dispatch(dataActions.setEventResult({ ...eventResults, [props.eventId as string]: json.results }))
        })
        .catch(err => console.log(err))
    } else {
      dispatch(settingsActions.setMessage({
        severity: Severity.error,
        message: `Ein verlorenes Spiel kann nicht ${t(`options:${gameProperty}`)} gespielt worden sein.`,
      }))
    }
  }

  const getMitOhneValue = (mitOhne?: MitOhne, factor?: number) => {
    let value = '1'

    if (mitOhne && factor) {
      switch (factor) {
        case 1:
          if (mitOhne === MitOhne.mit) {
            value = '5'
          } else {
            value = '4'
          }
          break
        case 2:
          if (mitOhne === MitOhne.mit) {
            value = '6'
          } else {
            value = '3'
          }
          break
        case 3:
          if (mitOhne === MitOhne.mit) {
            value = '7'
          } else {
            value = '2'
          }
          break
        case 4:
          if (mitOhne === MitOhne.mit) {
            value = '8'
          } else {
            value = '1'
          }
          break
      }
      return value
    }
  }

  return (
    <>
      {props.advancedEdit
        ? (
          <>
            <TableCell align='center'>
              <Typography variant='body1'>
                {props.round.gameValue || 0}
              </Typography>
            </TableCell>
            <TableCell>
              <Box sx={{ width: '150px' }}>
                <TextInputComponent
                  fullWidth
                  label='Spielwert'
                  value={player1Result}
                  onChange={(value) => {
                    value && setPlayer1Result(parseInt(value, 10))
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                  <CheckBoxComponent
                    label='gew.'
                    value={player === props.players[0] ? win : false}
                    onChange={(value) => {
                      setWin(value)
                      setPlayer(props.players[0])
                    }}
                  />
                  <CheckBoxComponent
                    label='verl.'
                    value={player === props.players[0] ? !win : false}
                    onChange={(value) => {
                      setWin(value)
                      setPlayer(props.players[0])
                    }}
                  />
                </Box>
              </Box>
            </TableCell>
            <TableCell>
              <Box sx={{ width: '150px' }}>
                <TextInputComponent
                  fullWidth
                  label='Spielwert'
                  value={player2Result}
                  onChange={(value) => {
                    value && setPlayer2Result(parseInt(value, 10))
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                  <CheckBoxComponent
                    label='gew.'
                    value={player === props.players[1] ? win : false}
                    onChange={(value) => {
                      setWin(value)
                      setPlayer(props.players[1])
                    }}
                  />
                  <CheckBoxComponent
                    label='verl.'
                    value={player === props.players[1] ? !win : false}
                    onChange={(value) => {
                      setWin(value)
                      setPlayer(props.players[1])
                    }}
                  />
                </Box>
              </Box>
            </TableCell>
            <TableCell>
              <Box sx={{ width: '150px' }}>
                <TextInputComponent
                  fullWidth
                  label='Spielwert'
                  value={player3Result}
                  onChange={(value) => {
                    value && setPlayer3Result(parseInt(value, 10))
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                  <CheckBoxComponent
                    label='gew.'
                    value={player === props.players[2] ? win : false}
                    onChange={(value) => {
                      setWin(value)
                      setPlayer(props.players[2])
                    }}
                  />
                  <CheckBoxComponent
                    label='verl.'
                    value={player === props.players[2] ? !win : false}
                    onChange={(value) => {
                      setWin(value)
                      setPlayer(props.players[2])
                    }}
                  />
                </Box>
              </Box>
            </TableCell>
            {player4 &&
              <TableCell>
                <Box sx={{ width: '150px' }}>
                  <TextInputComponent
                    fullWidth
                    label='Spielwert'
                    value={player4Result || 0}
                    onChange={(value) => {
                      value && setPlayer4Result(parseInt(value, 10))
                    }}
                  />
                  <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                    <CheckBoxComponent
                      label='gew.'
                      value={player === props.players[3] ? win : false}
                      onChange={(value) => {
                        setWin(value)
                        setPlayer(props.players[3])
                      }}
                    />
                    <CheckBoxComponent
                      label='verl.'
                      value={player === props.players[3] ? !win : false}
                      onChange={(value) => {
                        setWin(value)
                        setPlayer(props.players[3])
                      }}
                    />
                  </Box>
                </Box>
              </TableCell>}
          </>
        ) : (
          <TableCell colSpan={player4 ? 5 : 4}>
            <Grid container padding={2}>
              <Grid xs={downMd ? 12 : 2} sx={{ padding: '5px' }}>
                <DropDownComponent
                  fullWidth
                  value={game}
                  label='Spiel'
                  options={gameOptions}
                  onChange={(value) => {
                    if (value) {
                      setGame(value.key as Game)
                      if (value.key === Game.eingemischt) {
                        setWin(true)
                        setPlayer(props.dealer)
                        setFactor(undefined)
                        setGameProperty(undefined)
                        setMitOhne(undefined)
                        setHand(undefined)
                      }
                    } else {
                      setPlayer(undefined)
                      setGame(undefined)
                    }
                  }}
                />
              </Grid>
              <Grid xs={downMd ? 12 : 2} sx={{ padding: '5px' }}>
                {game && game !== Game.zero && game !== Game.eingemischt &&
                  <DropDownComponent
                    fullWidth
                    value={getMitOhneValue(mitOhne, factor)}
                    label='Mit / Ohne'
                    options={mitOhneOptions}
                    onChange={(value) => {
                      let tempMitOhne = MitOhne.mit
                      let tempFactor = 1
                      switch (value?.key) {
                        case '1':
                          tempMitOhne = MitOhne.ohne
                          tempFactor = 4
                          break
                        case '2':
                          tempMitOhne = MitOhne.ohne
                          tempFactor = 3
                          break
                        case '3':
                          tempMitOhne = MitOhne.ohne
                          tempFactor = 2
                          break
                        case '4':
                          tempMitOhne = MitOhne.ohne
                          tempFactor = 1
                          break
                        case '5':
                          tempMitOhne = MitOhne.mit
                          tempFactor = 1
                          break
                        case '6':
                          tempMitOhne = MitOhne.mit
                          tempFactor = 2
                          break
                        case '7':
                          tempMitOhne = MitOhne.mit
                          tempFactor = 3
                          break
                        case '8':
                          tempMitOhne = MitOhne.mit
                          tempFactor = 4
                          break
                      }

                      setMitOhne(tempMitOhne)
                      setFactor(tempFactor)
                    }}
                  />}
              </Grid>
              <Grid xs={downMd ? 12 : 3} sx={{ padding: '5px' }}>
                {game && game !== Game.eingemischt &&
                  <DropDownComponent
                    fullWidth
                    value={gameProperty}
                    label='Gespielt'
                    options={gamePropertyOptions}
                    onChange={(value) => {
                      if (value) {
                        setGameProperty(value.key as GameProperty)
                      } else {
                        setGameProperty(undefined)
                      }
                      if (value?.key.startsWith('hand')) {
                        setHand(true)
                      } else {
                        setHand(false)
                      }
                    }}
                  />}
              </Grid>
              <Grid xs={downMd ? 12 : 2} sx={{ padding: '5px' }}>
                {game &&
                  <DropDownComponent
                    fullWidth
                    value={player}
                    label={game === Game.eingemischt ? 'Kartengeber' : 'Spieler'}
                    options={getGamerOptions()}
                    onChange={(value) => {
                      if (value) {
                        setPlayer(value.key as string)
                      } else {
                        setPlayer(undefined)
                      }
                    }}
                  />}
              </Grid>
              <Grid xs={downMd ? 12 : 2} sx={{ padding: '5px' }}>
                <Box sx={{ height: '56px', marginTop: '8px', display: 'flex', alignItems: 'center' }}>
                  <CheckBoxComponent
                    label='Gewonnen?' disabled={game === Game.eingemischt}
                    value={win}
                    onChange={(value) => {
                      setWin(value)
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </TableCell>
        )}
      <TableCell>
        <IconButton
          disabled={!game && !player}
          onClick={() => saveResult()}
        ><SaveIcon />
        </IconButton>
      </TableCell>
    </>)
}

export default EditResultRound
